import { useQuery } from "@tanstack/react-query";
import { data, portal } from "@aawaz/api-client";
import { Badge, Card, Empty, ErrorBanner, KV, Loading, PageHeader, Stat, Table, fmtDate, humanize } from "@aawaz/ui";

interface FieldAcc { corrected: number; accuracy: number }
interface AccClass { class: string; bills: number; no_fix_needed: number; fields: Record<string, FieldAcc> }
interface TrustOut {
  storage: { region: string; files: string; keys: string; encrypted: string[] };
  channels: { whatsapp: string; writes: string };
  retention: { documents_years: number; quarantine_days: number; webhook_payloads_days: number };
  connections: { name: string; connector: string; status: string; write_back: boolean }[];
  consents: { connection: string; connector: string; kind: string; by: string; at: string }[];
  access_30d: Record<string, number>;
  recent_document_access: { action: string; by: string; target: string; at: string }[];
  sent_for_you_30d: { acknowledgements: number; document_reminders: number; supplier_nudges: number; auto_posted: number; voice_replies: number };
  accuracy: { window_days: number; bills: number; classes: AccClass[] };
  export: string;
}
const pct = (v: number) => `${(v * 100).toFixed(v === 1 ? 0 : 1)}%`;

/** Trust & data: where the firm's data is, how it is protected and kept, who opened documents, what AawazAI sent
 *  on the firm's behalf, and how often people had to correct the reader. */
export function TrustPage({ embedded }: { embedded?: boolean } = {}) {
  const q = useQuery({ queryKey: ["ca-trust"], queryFn: () => data<TrustOut>(portal.GET("/portal/v1/ca/trust")) });
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBanner error={q.error} />;
  const d = q.data;
  const sent = d.sent_for_you_30d;
  return (
    <>
      {embedded ? null : <PageHeader title="Trust & data" description="Where your clients' documents live, who touched them, and what AawazAI did for you." />}
      <div className="ui-stats">
        <Stat label="Documents opened (30 days)" value={d.access_30d["item.content.read"] ?? 0} icon="file" />
        <Stat label="Acknowledgements sent" value={sent.acknowledgements} icon="message" note="replies to your clients' uploads" />
        <Stat label="Reminders and nudges" value={sent.document_reminders + sent.supplier_nudges} icon="zap" />
        <Stat label="Bills auto-posted" value={sent.auto_posted} icon="check" note="verified e-invoices only" />
      </div>
      <Card title="Where your data is">
        <KV items={[["Region", d.storage.region], ["Files", d.storage.files], ["Encryption keys", d.storage.keys],
                    ["Encrypted", d.storage.encrypted.join(", ")], ["WhatsApp", d.channels.whatsapp], ["Writing into books", d.channels.writes],
                    ["Kept for", `${d.retention.documents_years} years (documents); quarantined files ${d.retention.quarantine_days} days`],
                    ["Your data, any time", d.export]]} />
      </Card>
      <Card title={`Reading accuracy · last ${d.accuracy.window_days} days`}>
        {d.accuracy.classes.length === 0 ? <Empty>No bills read yet.</Empty> : (
          <Table rows={d.accuracy.classes} rowKey={(r) => r.class} columns={[
            { key: "c", header: "Kind of bill", render: (r) => <strong>{humanize(r.class)}</strong> },
            { key: "n", header: "Bills", render: (r) => r.bills },
            { key: "o", header: "No fix needed", render: (r) => pct(r.no_fix_needed) },
            { key: "g", header: "GSTIN", render: (r) => pct(r.fields.supplier_gstin?.accuracy ?? 1) },
            { key: "i", header: "Invoice no", render: (r) => pct(r.fields.invoice_number?.accuracy ?? 1) },
            { key: "t", header: "Total", render: (r) => pct(r.fields.total?.accuracy ?? 1) },
          ]} />
        )}
        <p className="ui-hint">Share of bills where nobody had to correct the field. Corrections you make in the review queue keep improving the reader.</p>
      </Card>
      <Card title="Connected accounts and consent">
        <Table rows={d.connections} rowKey={(r) => `${r.connector}-${r.name}`} columns={[
          { key: "n", header: "Account", render: (r) => <strong>{r.name}</strong> },
          { key: "c", header: "Type", render: (r) => humanize(r.connector) },
          { key: "s", header: "Status", render: (r) => <Badge value={r.status} /> },
          { key: "w", header: "May write", render: (r) => r.write_back ? <Badge value="write-back on" tone="amber" /> : "read only" },
        ]} />
        {d.consents.length ? (
          <ul className="ui-hint" style={{ marginTop: 12, paddingLeft: 18 }}>
            {d.consents.slice(0, 8).map((c, i) => <li key={i}>{fmtDate(c.at)}: {humanize(c.kind)} for {c.connection} by {c.by}</li>)}
          </ul>) : null}
      </Card>
      <Card title="Recent document access">
        {d.recent_document_access.length === 0 ? <Empty>Nobody opened a document in the last 30 days.</Empty> : (
          <Table rows={d.recent_document_access} rowKey={(r) => `${r.at}-${r.target}`} columns={[
            { key: "a", header: "When", render: (r) => fmtDate(r.at) },
            { key: "b", header: "Who", render: (r) => <span className="ui-mono">{r.by}</span> },
            { key: "t", header: "Document", render: (r) => <span className="ui-mono">{r.target}</span> },
          ]} />
        )}
      </Card>
    </>
  );
}
